/**
 * Custom React hook to observe OneTrust consent model loading.
 *
 * This hook ensures that the OnetrustActiveGroups is captured when available.
 * It uses MutationObserver to watch for OneTrust initialization in the DOM.
 * Evaluates consent as soon as active groups exist, and registers
 * globalThis.OneTrust.OnConsentChanged once the SDK exposes it (which can be
 * later). Must be rendered above any component that reads
 * oneTrustConsentAtom (e.g. MarketingMFE).
 */
import hasTargetingCookieConsent from 'helpers/hasTargetingCookieConsent'
import { useEffect, useRef, useState } from 'react'
import { useSetAtom } from 'jotai'
import { oneTrustConsentAtom, oneTrustConsentLoadedAtom } from 'store/atoms'
import triggerOneTrustOnLoad, {
  isOneTrustReady
} from 'utils/triggerOneTrustOnLoad'

export function useOnetrustActiveGroups(): void {
  const [isOneTrustLoaded, setIsOneTrustLoaded] = useState(false)
  const [isConsentApiReady, setIsConsentApiReady] = useState(false)
  const setConsent = useSetAtom(oneTrustConsentAtom)
  const setConsentLoaded = useSetAtom(oneTrustConsentLoadedAtom)
  const listenerRegistered = useRef(false)

  // Waits for active groups only, so consent is evaluated as early as possible.
  useEffect(() => {
    return triggerOneTrustOnLoad(isOneTrustLoaded, setIsOneTrustLoaded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Waits for the SDK's OnConsentChanged, which can load after active groups.
  useEffect(() => {
    return triggerOneTrustOnLoad(
      isConsentApiReady,
      setIsConsentApiReady,
      isOneTrustReady
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isOneTrustLoaded) {
      setConsent(hasTargetingCookieConsent())
      /*
       * Flip once, regardless of value, so consumers can tell
       * "consent has been evaluated" apart from "consent is false".
       */
      setConsentLoaded(true)
    }
  }, [isOneTrustLoaded, setConsent, setConsentLoaded])

  useEffect(() => {
    if (isConsentApiReady && !listenerRegistered.current) {
      listenerRegistered.current = true
      // Re-sync in case consent changed before the listener could be registered.
      setConsent(hasTargetingCookieConsent())
      globalThis.OneTrust.OnConsentChanged(() => {
        setConsent(hasTargetingCookieConsent())
      })
    }
  }, [isConsentApiReady, setConsent])
}
